import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { BehaviorSubject, Observable, of, switchMap, merge, Subscription, combineLatest } from 'rxjs';
import { filter, map, shareReplay, tap, take, withLatestFrom } from 'rxjs/operators';
import { ToastMessageOptions, MessageService } from 'primeng/api';

import { ThemeApiService } from '../../../api/social/theme.api.service';
import { ThemeApi } from '../../../api/social/model/theme.api.model';

import { MediaSubscription } from '../../../Monitor/Shared/models/media-subscription.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { SocialRoutingService } from '../../Core/services/social-routing.service';

import { AgentService } from './agent.service';
import { SocialRouteModel } from '../../SocialRouter/models/social-route.model';
import { Theme } from '../models/theme.model';

import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { SocialRouteTypeEnum } from '../../../Social/SocialRouter/enums/social-route-type.enum';
import { CurrentResourceAbstractService } from '@Common/Basic/service/current-resource-abstract.service';
import { MultiBaseAbstractApi } from '@api/genericApi/multi-base-abstract.api';
import { ResourceActionTypes } from '@Common/Basic/enums/resource-actions.enum';
import { ResourceAction } from '@Common/Basic/service/resource-abstract.service';
import { CommonApiErrorEnum } from '@api/genericApi/enums/common-api-error.enum';

/**
 * No dirty state tracking needed, theme updates are immediately persisted
 * to the backend with no pending/dirty state.
 */
@Injectable()
export class ThemeService extends CurrentResourceAbstractService<Theme, ThemeApi, Theme, ThemeApi> {

    all$: Observable<Theme[]> = this.currentUserStateService.hasSocialAccess().pipe(
        switchMap((hasPermission) =>
            hasPermission ? merge(
                this.getApiService().getList().pipe(
                    map(list => list
                        .map(item => this.constructResourceModel(item))
                        .sort((a, b) => a.name.localeCompare(b.name))
                    )
                ),
                this.refreshAllManualSubject
            ) : of([])
        ),
        shareReplay(1)
    );

    currentResource$: Observable<Theme> = this.currentResourceSubject.asObservable().pipe(
        shareReplay(1)
    );


    /**
     * Maps `theme.id` to associated agents, using subscriptionIds
     */
    themeAgentMap$: Observable<Map<number, MediaSubscription[]>> = combineLatest([
        this.all$,
        this.agentService.all$
    ]).pipe(
        map(([themes, agents]) => {
            const agentMap = new Map(agents.map(agent => [agent.id, agent]));
            const themeAgentMap = new Map(
                themes.map(theme => {
                    const themeAgents = theme.subscriptionIds.flatMap(agentId => {
                        const agent = agentMap.get(agentId);
                        return agent ? [agent] : [];
                    });

                    return [theme.id, themeAgents];
                })
            );

            return themeAgentMap;
        }),
        shareReplay(1)
    );

    /**
     * Maps `agent.id` to associated themes, using subscriptionIds
     */
    agentThemeMap$: Observable<Map<number, Theme[]>> = combineLatest([
        this.agentService.all$,
        this.all$
    ]).pipe(
        map(([agents, themes]) => {
            const themeMap = new Map<number, Set<number>>(themes.map(theme => ([theme.id, new Set(theme.subscriptionIds)])));

            const agentThemeMap = new Map(
                agents.map(agent => {
                    const agentThemes = themes.filter(theme => {
                        const agentIdSet = themeMap.get(theme.id);
                        return agentIdSet?.has(agent.id);
                    });

                    return [agent.id, agentThemes];
                })
            );

            return agentThemeMap;
        }),
        shareReplay(1)
    );

    private quotaPanelVisibility$ = new BehaviorSubject<boolean>(true);

    protected subscriptions: Subscription[] = [];

    constructor(
        private agentService: AgentService,
        private socialRoutingService: SocialRoutingService,
        private themeApiService: ThemeApiService,
        private currentUserStateService: CurrentUserStateService,
        private messageService: MessageService,
        protected coreDialogService: CoreDialogService,
        protected localizationService: LocalizationService,
        private titleService: Title,
    ) {
        super();
        this.subscriptions.push(
            this.socialRoutingService.getCurrentRoute$().pipe(
                withLatestFrom(this.currentResource$),
                filter(([route, resource]) => !!route && resource?.id !== route?.value),
                switchMap(([route]) => this.onUpdateRoute(route))
            ).subscribe()
        );
    }


    getApiService(): MultiBaseAbstractApi<ThemeApi, ThemeApi> {
        return this.themeApiService;
    }

    constructListModel(apiModel: ThemeApi): Theme {
        return new Theme(apiModel);
    }

    constructResourceModel(apiModel: ThemeApi): Theme {
        return new Theme(apiModel);
    }

    refreshAllManual(): Observable<Theme[]> {
        return this.getApiService().getList().pipe(
            map(list => list
                .map(item => this.constructListModel(item))
                .sort((a, b) => a.name.localeCompare(b.name))
            ),
            tap((list) => this.refreshAllManualSubject.next(list)),
            take(1)
        );
    }

    create(theme: Theme): Observable<number> {
        return this.createResource(theme).pipe(
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }


    delete(theme: Theme): Observable<number> {
        return this.deleteResource(theme.id,{
            actionType: ResourceActionTypes.DELETE,
            resource: theme
        }).pipe(
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }

    /**
     * Rename the specified theme (via id) with the new name and force refresh of all$ before returning themeId.
     */
    renameDuplicateCheck(id: number, name: string): Observable<number> {
        return this.isNameDuplicated$(name, id).pipe(
            switchMap((duplicate) => {
                if (duplicate) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Social.intelligence.themeManagement.update.error.heading',
                        detail: 'Social.intelligence.themeManagement.update.duplicateNameError.message'
                    });
                    throw new Error(CommonApiErrorEnum.duplicateName);
                } else {
                    return this.rename(id, name);
                }
            })
        );
    }

    private rename(themeId: number, name: string): Observable<number> {
        return this.getById(themeId).pipe(
            switchMap((theme) => {
                theme.name = name;
                return this.updateResource(theme, {
                    actionType: ResourceActionTypes.UPDATE,
                });
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }


    update(theme: Theme, action?: ResourceAction): Observable<number> {
        const resourceAction = action ?? { actionType: ResourceActionTypes.UPDATE };
        return this.updateResource(theme, resourceAction).pipe(
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            }),
        );
    }

    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as Theme;

        switch(actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Social.intelligence.themeManagement.create.error.heading',
                        detail: this.localizationService.transform('Social.intelligence.themeManagement.create.error.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary: 'Social.intelligence.themeManagement.create.success.heading',
                        detail: this.localizationService.transform('Social.intelligence.themeManagement.create.success.message', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.UPDATE: {
                if (error) {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Social.intelligence.themeManagement.update.error.heading',
                        detail: this.localizationService.transform('Social.intelligence.themeManagement.update.error.message', {
                            name: resource.name
                        })
                    });
                } else {
                    if (actionResult.removedAgentName) {
                        this.messageService.add({
                            severity : messageSeverityEnum.success,
                            summary: 'Social.intelligence.themeManagement.update.success.heading',
                            detail: this.localizationService.transform('Social.intelligence.themeManagement.update.success.removeAgent', {
                                name: actionResult.removedAgentName
                            })
                        });
                    } else {
                        this.messageService.add({
                            severity : messageSeverityEnum.success,
                            summary: 'Social.intelligence.themeManagement.update.success.heading',
                            detail: this.localizationService.transform('Social.intelligence.themeManagement.update.success.message', {
                                name: resource.name
                            })
                        });
                    }
                }

                break;
            }
            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Social.intelligence.themeManagement.delete.error.heading',
                        detail: this.localizationService.transform('Social.intelligence.themeManagement.delete.error.message', {
                            name: resource.name
                        })
                    });
                } else {
                    const message: ToastMessageOptions = {
                        severity : messageSeverityEnum.success,
                        summary: 'Social.intelligence.themeManagement.delete.success.heading',
                        detail: this.localizationService.transform('Social.intelligence.themeManagement.delete.success.message', {
                            name: resource.name
                        })
                    };


                    this.messageService.add(message);
                }

                break;
            }
        }
    }


    getAgentsForTheme(themeId: number): Observable<MediaSubscription[]> {
        return this.themeAgentMap$.pipe(
            map(themeAgentMap => themeAgentMap.get(themeId)?.sort((a, b) => a.name.localeCompare(b.name)) || [])
        );
    }

    toggleQuotaVisibility(state: boolean) {
        this.quotaPanelVisibility$.next(state);
    }

    getQuotaPanelVisibility(): Observable<boolean> {
        return this.quotaPanelVisibility$;
    }

    private onUpdateRoute(route: SocialRouteModel): Observable<Theme> {
        const id: number = +route.value;
        const type: SocialRouteTypeEnum = route.type;

        if ([SocialRouteTypeEnum.analytics, SocialRouteTypeEnum.postFeed].includes(type)) {
            if (!id) {
                this.openThemeNotFoundDialog();
            } else {
                return this.all$.pipe(
                    take(1),
                    map(themes => {
                        const currentTheme = themes.find(x => x.id === id);

                        if (currentTheme) {
                            this.setCurrentResource(currentTheme);
                            this.titleService.setTitle(
                                this.localizationService.transform('Social.themes.browserTitle.name', { name: currentTheme.name })
                            );
                        } else {
                            this.openThemeNotFoundDialog();
                            return null;
                        }
                    })
                );
            }
        }

        return of(null);
    }

    private openThemeNotFoundDialog() {
        this.coreDialogService.open(ConfirmDialogComponent, {
            inputs: {
                heading: 'Social.intelligence.themeNotFound.heading',
                message: 'Social.intelligence.themeNotFound.message',
                okLabel: 'Social.intelligence.themeNotFound.acknowledge',
                showCancel: false,
                confirmAction: () => {
                    this.socialRoutingService.gotoRootPath();
                    return of(true);
                }
            }
        });
    }
}
